import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Pick = ({ value, onChange, options, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-10" data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
    <SelectContent>{options.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
  </Select>
);

const fromUser = (u, roles) => ({
  name: u?.name || "", email: u?.email || "", password: "",
  role: u?.role || roles.find((r) => r.key === "manager")?.key || roles[0]?.key || "",
  department: u?.department || "none", supervisor_id: u?.supervisor_id || "none",
});

export const UserFormDialog = ({ open, user, roles, departments, supervisors, isSuper, onClose, onSaved }) => {
  const [f, setF] = useState(fromUser(null, roles));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setF(fromUser(user, roles));
  }, [open, user, roles]);

  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));

  const save = async () => {
    const body = {
      name: f.name, email: f.email, role: f.role,
      department: f.department === "none" ? null : f.department,
      supervisor_id: f.supervisor_id === "none" ? null : f.supervisor_id,
    };
    if (f.password) body.password = f.password;
    setBusy(true);
    try {
      if (user) await api.patch(`/admin/users/${user.id}`, body);
      else await api.post("/admin/users", body);
      toast.success(user ? "User updated" : "User added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const roleOptions = [...(isSuper ? [["admin", "Super Admin"]] : []), ...roles.map((r) => [r.key, r.name])];
  const supOptions = [["none", "Unassigned"], ...supervisors.filter((s) => s.id !== user?.id).map((s) => [s.id, s.name])];
  const lockedRole = user?.role === "admin" && !isSuper;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="user-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#24474A]">{user ? "Edit user" : "Add team member"}</DialogTitle>
          <DialogDescription>{user ? "Leave password empty to keep the current one." : "Share the email and temporary password with them directly."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Full name</Label><Input value={f.name} onChange={set("name")} data-testid="user-form-name-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="user-form-email-input" /></div>
          <div className="space-y-1.5"><Label>{user ? "New password (optional)" : "Temporary password"}</Label><Input type="text" value={f.password} onChange={set("password")} data-testid="user-form-password-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Role</Label>
              {lockedRole ? <Input disabled value="Super Admin" /> : <Pick value={f.role} onChange={set("role")} options={roleOptions} testid="user-form-role-select" />}
            </div>
            <div className="space-y-1.5"><Label>Department</Label><Pick value={f.department} onChange={set("department")} options={[["none", "None"], ...departments.map((d) => [d.name, d.name])]} testid="user-form-dept-select" /></div>
          </div>
          <div className="space-y-1.5"><Label>Supervisor</Label><Pick value={f.supervisor_id} onChange={set("supervisor_id")} options={supOptions} testid="user-form-supervisor-select" /></div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full" onClick={save} disabled={busy || !f.name || !f.email || (!user && !f.password)} data-testid="user-form-save-btn">{user ? "Save changes" : "Add user"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
